import { useState, useEffect } from 'react';
import { ChatWindow, ChatWindowLegacy, FloatingChat, TurnstileGuard } from './components';
import { usePartyKit, useLanguage, useFileUpload } from './hooks';
import { getPartnerConfig, isFeatureEnabled } from './config';
import type { CustomerInfo, Message } from './types';
import './index.css';

// Configuration
const PARTYKIT_HOST = import.meta.env.VITE_PARTYKIT_HOST || 'kronium-embed-chat.nlk-jan.partykit.dev';
// Turnstile site key - use invisible test key for debugging: 1x00000000000000000000AA
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '1x00000000000000000000AA';
const FILE_SERVICE_URL = import.meta.env.VITE_FILE_SERVICE_URL || 'https://kronium-file-service.neurologik-account.workers.dev';

// Get mode from URL params
function getMode(): 'fullpage' | 'floating' | 'inline' {
  const params = new URLSearchParams(window.location.search);
  const mode = params.get('mode');
  if (mode === 'floating' || mode === 'inline') return mode;
  return 'fullpage';
}

// Get UI version from URL params (?ui=legacy for old drawer-based UI)
function getUIVersion(): 'legacy' | 'tabs' {
  const params = new URLSearchParams(window.location.search);
  return params.get('ui') === 'legacy' ? 'legacy' : 'tabs';
}

function ChatApp({ turnstileToken }: { turnstileToken: string | null }) {
  const config = getPartnerConfig();
  const partnerId = config.partner_id;
  const mode = getMode();
  const uiVersion = getUIVersion();

  // Check feature flags
  const fileUploadEnabled = isFeatureEnabled('file_upload');
  const researchModeEnabled = isFeatureEnabled('research_mode');

  // Select ChatWindow component based on UI version
  const ChatWindowComponent = uiVersion === 'tabs' ? ChatWindow : ChatWindowLegacy;

  const {
    state,
    sendMessage,
    setCustomer,
    dismissContactDrawer,
    openContactDrawer,
    sendIntentEvent,
    sendLanguageChange,
    visitorId,
    // Research mode (only used if enabled)
    sendVerifyEmailRequest,
    sendVerifyEmailConfirm,
    sendResearchModeSet,
    sendNotificationPrefsUpdate,
  } = usePartyKit({
    partnerId: config.partner_id,
    partyKitHost: PARTYKIT_HOST,
    turnstileToken,
  });

  // File upload management (only if feature enabled)
  const {
    pendingFiles,
    addFiles,
    removeFile,
    clearAll: clearPendingFiles,
  } = useFileUpload({
    fileServiceUrl: FILE_SERVICE_URL,
    partnerId,
    sessionId: visitorId,
    onUploadComplete: (file) => {
      if (!fileUploadEnabled) return;
      console.log('File uploaded:', file);

      const fileMessage: Message = {
        id: `file-${file.file_id}`,
        role: 'system',
        type: 'file_upload',
        content: '',
        metadata: {
          filename: file.filename,
          size: file.file_size,
          fileType: file.file_type,
          uploadedBy: 'user',
          fileId: file.file_id,
        },
        timestamp: new Date(),
      };

      console.log('File upload message:', fileMessage);
    },
    onUploadError: (filename, error) => {
      if (!fileUploadEnabled) return;
      console.error(`Failed to upload ${filename}:`, error);
    },
  });

  // Initialize language management
  const languageConfig = {
    partner_language: config.language,
    supported_languages: config.supported_languages,
  };

  const {
    state: languageState,
    setLanguage,
    setCfLanguage,
    dismissCfSuggestion,
    isLanguageSupported,
  } = useLanguage(languageConfig);

  // Sync CF language from PartyKit state to useLanguage
  useEffect(() => {
    if (state.cf_language) {
      setCfLanguage(state.cf_language);
    }
  }, [state.cf_language, setCfLanguage]);

  // Handle EdgeLLM detected language - only accept if supported
  useEffect(() => {
    if (state.detected_language) {
      if (isLanguageSupported(state.detected_language)) {
        if (state.detected_language !== languageState.current) {
          setLanguage(state.detected_language, 'edge_llm');
          sendLanguageChange(state.detected_language, 'edge_llm', languageState.current);
        }
        dismissCfSuggestion();
      } else {
        console.warn(`[App] EdgeLLM detected unsupported language: ${state.detected_language}, ignoring`);
      }
    }
  }, [state.detected_language, languageState.current, setLanguage, dismissCfSuggestion, sendLanguageChange, isLanguageSupported]);

  // Sync language to server on WebSocket (re)connect
  useEffect(() => {
    if (state.isConnected && languageState.current) {
      sendLanguageChange(languageState.current, 'restored', 'en');
    }
  }, [state.isConnected, languageState.current, sendLanguageChange]);

  const handleCustomerSubmit = (customer: Partial<CustomerInfo>) => {
    setCustomer(customer);

    if (state.drawerTriggerIntent && customer.email) {
      sendIntentEvent({
        intent_type: state.drawerTriggerIntent,
        thread_id: `partykit_${partnerId}_${Date.now()}`,
        partner_id: partnerId,
        customer: {
          email: customer.email,
          name: customer.name,
          country: customer.country || config?.contact_form.default_country || 'CZ',
          phone: customer.phone,
        },
        timestamp: new Date().toISOString(),
      });
    }
  };

  const handleLanguageSelect = (code: string) => {
    setLanguage(code, 'user_explicit');
    sendLanguageChange(code, 'user_explicit', languageState.current);
  };

  const handleSendMessage = (message: string) => {
    // Only include files if file upload is enabled
    const uploadedFiles = fileUploadEnabled
      ? pendingFiles
          .filter((pf) => pf.status === 'uploaded')
          .map((pf) => ({
            file_id: pf.id,
            filename: pf.file.name,
            file_type: pf.file.type,
            file_size: pf.file.size,
          }))
      : [];

    sendMessage(message, uploadedFiles.length > 0 ? uploadedFiles : undefined);

    if (fileUploadEnabled && pendingFiles.length > 0) {
      setTimeout(() => {
        clearPendingFiles();
      }, 0);
    }
  };

  // Common props for all ChatWindow variants
  const chatProps = {
    config,
    onSendMessage: handleSendMessage,
    state,
    onCustomerSubmit: handleCustomerSubmit,
    onDrawerDismiss: dismissContactDrawer,
    onDrawerOpen: openContactDrawer,
    // Feature flags to control UI visibility
    fileUploadEnabled,
    researchModeEnabled,
    // File upload (only if enabled)
    pendingFiles: fileUploadEnabled ? pendingFiles : [],
    onFilesSelected: fileUploadEnabled ? addFiles : () => {},
    onFileRemove: fileUploadEnabled ? removeFile : () => {},
    // Language
    languageState,
    onSelectLanguage: handleLanguageSelect,
    onDismissLanguageSuggestion: dismissCfSuggestion,
    // Research mode (only if enabled)
    onVerifyEmail: researchModeEnabled ? sendVerifyEmailRequest : () => {},
    onConfirmOtp: researchModeEnabled ? sendVerifyEmailConfirm : () => {},
    onResendOtp: researchModeEnabled ? sendVerifyEmailRequest : () => {},
    onToggleResearchMode: researchModeEnabled ? sendResearchModeSet : () => {},
    onUpdateNotificationPrefs: researchModeEnabled ? sendNotificationPrefsUpdate : () => {},
  };

  if (mode === 'floating') {
    return (
      <FloatingChat
        {...chatProps}
        ChatWindowComponent={ChatWindowComponent}
      />
    );
  }

  if (mode === 'inline') {
    return (
      <div className="w-full h-[500px] rounded-lg overflow-hidden shadow-lg">
        <ChatWindowComponent {...chatProps} />
      </div>
    );
  }

  return (
    <div className="h-full w-full">
      <ChatWindowComponent {...chatProps} />
    </div>
  );
}

function App() {
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  return (
    <TurnstileGuard siteKey={TURNSTILE_SITE_KEY} onVerified={setTurnstileToken}>
      <ChatApp turnstileToken={turnstileToken} />
    </TurnstileGuard>
  );
}

export default App;
